import { Drawer } from "antd"
import CheckBoxSelected from "icons/checkbox-selected.svg"
import CheckBox from "icons/checkbox.svg"
import { useEffect, useState } from "react"
import { cls } from "reactutils"

export type SysNo = string | number

export type LabelKey = "name" | "label"

export type SubItem = {
  [label in LabelKey]?: string
} & {
  sysNo: SysNo
  children?: Array<SubItem>
}

export type Value = {
  [key: string]: SysNo | Array<SysNo>
}

export type Item = {
  type: "checkbox" | "radio"
  title: string
  labelKey: LabelKey // subItem中可能为name或者label两个字段
  prop: string // 筛选条件字段名称
  show: boolean
  hotSubList: Array<SubItem>
  hasMoreBtn: boolean
  totalSubList?: Array<SubItem>
}

export type SearchConditionDrawerProps = {
  visible: boolean
  value: Value
  conditionList: Array<Item>
  onChange: (value: Value, prop: any) => void
  onClose: () => void
}

export default function SearchConditionDrawer({
  visible,
  conditionList = [],
  value,
  onClose,
  onChange,
}: SearchConditionDrawerProps) {
  const [visibleMoreDrawer, setVisibleMoreDrawer] = useState(false)
  const [activeMoreItem, setActiveMoreItem] = useState<Item>({
    type: "checkbox",
    title: "",
    labelKey: "name",
    prop: "",
    show: true,
    hotSubList: [],
    totalSubList: [],
    hasMoreBtn: false,
  })

  function isSubItemActive({ type, prop }: Item, { sysNo }: SubItem): boolean {
    if (!value) return false
    let flag = false
    if (type === "checkbox") {
      const checkboxVal = (value[prop] || []) as Array<SysNo>
      flag = !!checkboxVal?.find((selectItem) => selectItem === sysNo)
    } else {
      const radioVal = value[prop]
      flag = radioVal === sysNo
    }
    return flag
  }

  function isSubMoreActive({ type, prop, hotSubList }: Item): boolean {
    if (!value) return false
    let flag = false
    if (type === "checkbox") {
      const checkboxVal = (value[prop] || []) as Array<SysNo>
      flag = !!checkboxVal?.find(
        (selectItem) =>
          !hotSubList?.find((hotItem) => hotItem.sysNo === selectItem),
      )
    } else {
      const radioVal = value[prop]
      flag = !hotSubList?.find((hotItem) => hotItem.sysNo === radioVal)
    }
    return flag
  }

  function handleClickItem({ type, prop }: Item, { sysNo }: SubItem) {
    const draftValue = { ...value }
    if (type === "checkbox") {
      const checkboxVal = (draftValue[prop] || []) as Array<SysNo>
      const index = checkboxVal?.findIndex((selectItem) => selectItem === sysNo)
      if (index === -1) {
        checkboxVal?.push(sysNo)
      } else {
        checkboxVal?.splice(index, 1)
      }
      draftValue[prop] = checkboxVal
    } else {
      const radioVal = draftValue[prop]
      if (radioVal === sysNo) {
        draftValue[prop] = ""
      } else {
        draftValue[prop] = sysNo
      }
    }
    onChange(draftValue, prop)
  }

  function handleReset() {
    const newValue: Value = {}
    conditionList?.forEach((item) => {
      if (item?.type === "checkbox") {
        newValue[item?.prop] = []
      } else {
        newValue[item?.prop] = ""
      }
    })
    onChange(newValue, "reset")
  }

  function showMoreDrawer(item: Item) {
    setActiveMoreItem(item)
    setVisibleMoreDrawer(true)
  }

  function handleChangeMore(
    { prop }: Item,
    subItemValue: SysNo | Array<SysNo>,
  ) {
    const draftValue = { ...value }
    draftValue[prop] = subItemValue
    onChange(draftValue, prop)
  }

  return (
    <>
      <Drawer
        width="320px"
        className="search-drawer"
        visible={visible}
        onClose={onClose}
        closable={false}
      >
        <div className="drawerContentWrap">
          <div className="conditionWrap">
            {conditionList?.map((item, index) =>
              item.show ? (
                <div className="itemWrap" key={index}>
                  <div className="itemTitle">
                    <span>{item.title}</span>
                    {item.type === "radio" && (
                      <span className="itemSubTitle">单选</span>
                    )}
                  </div>
                  <div className="subListWrap">
                    {item.hotSubList?.map((subItem, subIndex) => (
                      <div
                        className={cls(
                          "subItemWrap",
                          isSubItemActive(item, subItem) && "active",
                        )}
                        key={subIndex}
                        onClick={() => {
                          handleClickItem(item, subItem)
                        }}
                      >
                        {subItem[item.labelKey]}
                      </div>
                    ))}
                    {item.type === "checkbox" && item.hasMoreBtn && (
                      <div
                        onClick={() => {
                          showMoreDrawer(item)
                        }}
                        className={cls(
                          "subItemWrap",
                          "more",
                          isSubMoreActive(item) && "moreActive",
                        )}
                      >
                        {`更多${item.title}`}
                      </div>
                    )}
                  </div>
                </div>
              ) : null,
            )}
          </div>
          <SearchFooter onConfirm={onClose} onReset={handleReset} />
        </div>
      </Drawer>
      <SearchMoreDrawer
        visible={visibleMoreDrawer}
        title={activeMoreItem.title}
        labelKey={activeMoreItem.labelKey}
        value={(value[activeMoreItem.prop] || []) as Array<SysNo>}
        list={activeMoreItem.totalSubList || []}
        onClose={() => {
          setVisibleMoreDrawer(false)
        }}
        onChange={(subItemValue) => {
          handleChangeMore(activeMoreItem, subItemValue)
        }}
      />
      <style jsx>{`
        .drawerContentWrap {
          display: flex;
          height: 100vh;
          flex-direction: column;
        }

        .conditionWrap {
          flex: 1;
          overflow-y: auto;
        }

        .itemWrap {
          padding: 30px 20px 17px 20px;
          border-bottom: 2px solid #f9f9f9;
        }

        .itemWrap:last-child {
          border-bottom: none;
        }

        .itemTitle {
          font-size: 15px;
          color: #262626;
          line-height: 13px;
          font-weight: bold;
          margin-bottom: 17px;
        }

        .itemSubTitle {
          font-size: 12px;
          color: #999999;
          margin-left: 12px;
        }

        .subListWrap {
          margin-bottom: -14px;
        }

        .subItemWrap {
          width: 84px;
          height: 30px;
          line-height: 30px;
          display: inline-block;
          vertical-align: middle;
          text-align: center;
          background: #f5f5f5;
          margin-right: 14px;
          font-size: 14px;
          color: #262626;
          padding: 0 5px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          border-radius: 100px;
          box-sizing: border-box;
          margin-bottom: 14px;
        }

        .subItemWrap.active {
          background-color: #edf3ff;
          border-color: var(--primaryColor);
          color: var(--primaryColor);
        }

        .subItemWrap.more {
          background-color: #fff;
          border: 1px solid #c3c3c3;
          color: #595959;
        }

        .subItemWrap.more.moreActive {
          border-color: var(--primaryColor);
          color: var(--primaryColor);
        }

        .subItemWrap:nth-child(3n) {
          margin-right: 0;
        }
        :global(.search-drawer .ant-drawer-body) {
          padding: 0;
        }
      `}</style>
    </>
  )
}

type SearchMoreDrawerProps = {
  visible: boolean
  title: string
  labelKey: LabelKey
  value: Array<SysNo>
  list: Array<SubItem>
  onClose: () => void
  onChange: (value: Array<SysNo>) => void
}

function SearchMoreDrawer({
  visible,
  title,
  labelKey,
  value: initValue,
  list,
  onClose,
  onChange,
}: SearchMoreDrawerProps) {
  const [activeLeftIndex, setActiveLeftIndex] = useState(0)
  const [value, setValue] = useState<Array<SysNo>>([...initValue])
  const activeLeftSysNo = list?.[activeLeftIndex]?.sysNo

  useEffect(() => {
    setValue([...initValue])
  }, [initValue])

  useEffect(() => {
    if (visible) {
      setActiveLeftIndex(0)
    }
  }, [visible])

  function isActive({
    sysNo,
    isUnlimited,
  }: {
    sysNo: SysNo
    isUnlimited?: boolean
  }) {
    const isIncludes = value?.includes(sysNo)
    if (isUnlimited) {
      return isIncludes
    } else {
      // 如果已经勾选了不限，就不高亮选中显示
      return isIncludes && !value?.includes(activeLeftSysNo)
    }
  }

  function handleClickCheckBox(sysNo: any) {
    const draftValue = [...value]
    const indexRightSysNo = value?.findIndex(
      (selectSysNo) => selectSysNo === sysNo,
    )
    const indexLeftSysNo = value?.findIndex(
      (selectSysNo) => selectSysNo === activeLeftSysNo,
    )
    if (indexRightSysNo === -1) {
      // 该二级分类没有被勾选状态
      if (indexLeftSysNo !== -1) {
        // 如果"不限"已被勾选,取消"不限"的勾选
        draftValue?.splice(indexLeftSysNo, 1)
      }
      draftValue?.push(sysNo)
    } else {
      // 该二级分类已经是被勾选状态
      if (indexLeftSysNo !== -1) {
        /**
         * 如果"不限"也被勾选了，
         * 将其他二级分类和"不限"都取消勾选，
         * 然后将点击的二级分类勾选
         */
        clearOtherCheckBox(draftValue)
        draftValue?.splice(indexLeftSysNo, 1)
        draftValue?.push(sysNo)
      } else {
        draftValue?.splice(indexRightSysNo, 1)
      }
    }
    setValue(draftValue)
  }

  function handleClickUnlimited() {
    const draftValue = [...value]
    const index = value?.findIndex(
      (selectSysNo) => selectSysNo === activeLeftSysNo,
    )
    if (index === -1) {
      // 清除该分类下的二级分类，选中"不限"

      clearOtherCheckBox(draftValue)
      draftValue?.push(activeLeftSysNo)
    } else {
      // 取消勾选"不限"
      draftValue?.splice(index, 1)
    }
    setValue(draftValue)
  }

  function clearOtherCheckBox(draftValue: Array<SysNo>) {
    const rightList = list?.[activeLeftIndex]?.children
    rightList?.forEach((rightItem) => {
      const index = draftValue?.findIndex(
        (selectSysNo) => selectSysNo === rightItem.sysNo,
      )
      if (index !== -1) {
        draftValue?.splice(index, 1)
      }
    })
  }

  return (
    <Drawer
      width="320px"
      visible={visible}
      className="search-drawer"
      onClose={onClose}
      closable={false}
    >
      <div className="drawerContentWrap">
        <div className="conditionTitle">选择{title}</div>
        <div className="conditionWrap">
          <div className="leftWrap">
            {list?.map((leftItem, leftIndex) => (
              <div
                onClick={() => {
                  setActiveLeftIndex(leftIndex)
                }}
                key={leftIndex}
                className={cls(
                  "leftItem",
                  activeLeftIndex === leftIndex && "active",
                )}
              >
                {leftItem[labelKey]}
              </div>
            ))}
          </div>
          <div className="rightWrap">
            <div
              className="rightItem"
              onClick={() => {
                handleClickUnlimited()
              }}
            >
              <div
                className={cls(
                  "rightItemTxt",
                  isActive({ sysNo: activeLeftSysNo, isUnlimited: true }) &&
                    "activeTxt",
                )}
              >
                不限
              </div>
              {isActive({ sysNo: activeLeftSysNo, isUnlimited: true }) && (
                <span className="checkIcon"></span>
              )}
            </div>
            {list?.[activeLeftIndex]?.children?.map((rightItem, rightIndex) => (
              <div
                onClick={() => {
                  handleClickCheckBox(rightItem.sysNo)
                }}
                key={rightIndex}
                className="rightItem"
              >
                <div
                  className={cls(
                    "rightItemTxt",
                    isActive({ sysNo: rightItem.sysNo, isUnlimited: false }) &&
                      "activeTxt",
                  )}
                >
                  {rightItem[labelKey]}
                </div>
                <span className="checkBox">
                  {isActive({ sysNo: rightItem.sysNo, isUnlimited: false }) ? (
                    <CheckBoxSelected />
                  ) : (
                    <CheckBox />
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
        <SearchFooter
          onReset={() => {
            setValue([])
          }}
          onConfirm={() => {
            onChange(value)
            onClose()
          }}
        ></SearchFooter>
      </div>
      <style jsx>{`
        .drawerContentWrap {
          display: flex;
          height: 100vh;
          flex-direction: column;
          box-sizing: border-box;
        }

        .conditionTitle {
          font-size: 15px;
          color: #262626;
          line-height: 13px;
          padding: 25px 0 24px 22px;
          border-bottom: 1px solid #ededed;
          font-weight: bold;
        }

        .conditionWrap {
          flex: 1;
          overflow-y: auto;
          display: flex;
        }

        .leftWrap {
          flex: 0 0 120px;
          background-color: #f5f5f5;
          overflow-y: auto;
        }

        .leftItem {
          height: 50px;
          box-sizing: border-box;
          padding: 15px 5px 15px 22px;
          font-size: 14px;
          color: #262626;
        }

        .leftItem.active {
          background-color: #fff;
          color: var(--primaryColor);
        }

        .rightWrap {
          flex: 0 0 200px;
          overflow-y: auto;
        }

        .rightItem {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 17px 24px 13px 24px;
        }

        .rightItemTxt {
          font-size: 14px;
          color: #262626;
          padding-right: 8px;
        }

        .activeTxt {
          color: var(--primaryColor);
        }

        .checkBox {
          height: 16px;
          flex: 0 0 16px;
        }

        .checkIcon {
          width: 17px;
          height: 9px;
          border-left: 2px solid var(--primaryColor);
          border-bottom: 2px solid var(--primaryColor);
          transform: rotate(-45deg);
          display: inline-block;
        }
        :global(.search-drawer .ant-drawer-body) {
          padding: 0;
        }
      `}</style>
    </Drawer>
  )
}

type SearchFooterProps = {
  onReset: () => void
  onConfirm: () => void
}

export function SearchFooter({ onConfirm, onReset }: SearchFooterProps) {
  return (
    <>
      <div className="footerWrap">
        <Button
          circle
          onClick={onReset}
          className={cls("footerBtn", "resetBtn")}
        >
          重置
        </Button>
        <Button
          onClick={onConfirm}
          className="footerBtn confirmBtn"
          type="primary"
          circle
        >
          确认
        </Button>
      </div>
      <style jsx>{`
        .footerWrap {
          flex: 0 0 56px;
          background: #ffffff;
          box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.07);
          text-align: center;
          padding-top: 7px;
          box-sizing: border-box;
          display: flex;
          justify-content: space-around;
        }

        .footerBtn {
          margin-right: 20px;
          width: 136px;
          display: inline-block;
          vertical-align: middle;
          height: 44px;
          line-height: 44px;
        }

        .footerBtn:last-child {
          margin-right: 0;
        }

        .footerWrap :global(.resetBtn) {
          border-color: var(--primaryColor);
          color: var(--primaryColor);
        }
        .footerWrap :global(.confirmBtn) {
          background-color: var(--primaryColor);
        }
      `}</style>
    </>
  )
}

type ButtonProps = {
  type?: "default" | "primary"
  className?: string
  circle?: boolean
  disabled?: boolean
  children: React.ReactNode
  onClick?: () => void
}
function Button({
  type = "default",
  className,
  circle = false,
  disabled = false,
  children,
  onClick,
}: ButtonProps) {
  return (
    <>
      <div
        className={cls(
          "button",
          type === "default" && "button-default",
          type === "primary" && "button-primary",
          circle && "button-circle",
          disabled && "button-disabled",
          className,
        )}
        onClick={() => {
          !disabled && onClick && onClick()
        }}
      >
        {children}
      </div>
      <style jsx>{`
        .button {
          width: 136px;
          height: 44px;
          line-height: 44px;
          text-align: center;
          border-radius: 5px;
        }

        .button-default {
          border: 1px solid rgba(0, 0, 0, 0.2);
        }

        .button-primary {
          box-shadow: 0 2px 12px 0 rgba(31, 82, 249, 0.25);
          border: none;
          color: #ffffff;
        }

        .button-circle {
          border-radius: 50px;
        }

        .button-disabled {
          opacity: 0.4;
        }
      `}</style>
    </>
  )
}
